import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../utils/Customer/apiBaseQuery";
import {
  IAbandonedCartListRequest,
  IAbandonedCartRequest,
  ICreditUserRewardRequest,
  ICreditUserWalletRequest,
  ICustomerAddressRequest,
  ICustomerListRequest,
  ICustomerPetRequest,
  IPersonetRequest,
  IRewardTransactionListRequest,
  ISendBulkReminderRequest,
  IWalletTransactionListRequest,
} from "../../../types/Request/AdminRequest";
import {
  IAbandonedCartDashboardResponse,
  IAbandonedCartDetailsResponse,
  IAbandonedCartListResponse,
  ICustomerAddressDetailResponse,
  ICustomerAddressListResponse,
  ICustomerDetailsResponse,
  ICustomerListResponse,
  ICustomerOrdersResponse,
  ICustomerPetByIdResponse,
  ICustomerPetsResponse,
  ICustomerSubscriptionsResponse,
  ICustomerWalletResponse,
  IRewardTransactionListResponse,
  IRewardWalletResponse,
  IWalletCreditResponse,
  IWalletTransactionListResponse,
} from "../../../types/Response/AdminResponse";
import {
  ABANDONED_CARTS,
  ABANDONED_CARTS_DASHBOARD,
  ADMIN_CUSTOMERS,
  ADMIN_CUSTOMERS_REWARD_TRANSACTIONS,
  ADMIN_CUSTOMERS_WALLET_TRANSACTIONS,
  IMPERSONATE_CUSTOMER,
  SEND_REMINDERS,
} from "../../../config/apiUrlsAdmin";

export const adminAbandonedCartAPi = createApi({
  reducerPath: "adminAbandonedCartAPi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["abandonedCart", "wallet", "rewards", "addresses", "pets"],

  endpoints: (builder) => ({
    getCustomersForAdmin: builder.query<ICustomerListResponse, ICustomerListRequest>({
      query: (data) => ({ url: ADMIN_CUSTOMERS, method: "get", data }),
      providesTags: ["abandonedCart"],
    }),

    getCustomersForAdminById: builder.query<ICustomerDetailsResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}`, method: "get" }),
      providesTags: ["abandonedCart"],
    }),

    getCustomerWalletById: builder.query<ICustomerWalletResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}/wallet`, method: "get" }),
      providesTags: ["wallet"],
    }),

    creditCustomerWallet: builder.mutation<IWalletCreditResponse, { userId: number; data: ICreditUserWalletRequest }>({
      query: ({ userId, data }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/wallet/credit`,
        method: "post",
        data,
      }),
      invalidatesTags: ["wallet"],
    }),

    debitCustomerWallet: builder.mutation<IWalletCreditResponse, { userId: number; data: ICreditUserWalletRequest }>({
      query: ({ userId, data }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/wallet/debit`,
        method: "post",
        data,
      }),
      invalidatesTags: ["wallet"],
    }),

    walletTransaction: builder.query<IWalletTransactionListResponse, IWalletTransactionListRequest>({
      query: (data) => ({
        url: ADMIN_CUSTOMERS_WALLET_TRANSACTIONS,
        method: "get",
        data,
      }),
      providesTags: ["wallet"],
    }),

    userRewardPoints: builder.query<IRewardWalletResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}/reward-points`, method: "get" }),
      providesTags: ["rewards"],
    }),

    addCustomerRewardPoints: builder.mutation<IWalletCreditResponse, { userId: number; data: ICreditUserRewardRequest }>({
      query: ({ userId, data }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/reward-points/add`,
        method: "post",
        data,
      }),
      invalidatesTags: ["rewards"],
    }),

    deductCustomerRewardPoints: builder.mutation<IWalletCreditResponse, { userId: number; data: ICreditUserRewardRequest }>({
      query: ({ userId, data }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/reward-points/deduct`,
        method: "post",
        data,
      }),
      invalidatesTags: ["rewards"],
    }),

    rewardTransaction: builder.query<IRewardTransactionListResponse, IRewardTransactionListRequest>({
      query: (data) => ({
        url: ADMIN_CUSTOMERS_REWARD_TRANSACTIONS,
        method: "get",
        data,
      }),
      providesTags: ["rewards"],
    }),

    customerAddresses: builder.query<ICustomerAddressListResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}/addresses`, method: "get" }),
      providesTags: ["addresses"],
    }),

    customerAddressesById: builder.query<ICustomerAddressDetailResponse, { userId: number; addressId: number }>({
      query: ({ userId, addressId }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/addresses/${addressId}`,
        method: "get",
      }),
      providesTags: ["addresses"],
    }),

    updateCustomerAddressesById: builder.mutation<IWalletCreditResponse, { userId: number; addressId: number; data: ICustomerAddressRequest }>({
      query: ({ userId, addressId, data }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/addresses/${addressId}`,
        method: "put",
        data,
      }),
      invalidatesTags: ["addresses"],
    }),

    customerPets: builder.query<ICustomerPetsResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}/pets`, method: "get" }),
      providesTags: ["pets"],
    }),

    customerPetsById: builder.query<ICustomerPetByIdResponse, { userId: number; petId: number }>({
      query: ({ userId, petId }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/pets/${petId}`,
        method: "get",
      }),
      providesTags: ["pets"],
    }),

    UpdateCustomerPetsById: builder.mutation<IWalletCreditResponse, { userId: number; petId: number; data: ICustomerPetRequest }>({
      query: ({ userId, petId, data }) => ({
        url: `${ADMIN_CUSTOMERS}/${userId}/pets/${petId}`,
        method: "put",
        data,
      }),
      invalidatesTags: ["pets"],
    }),

    customerOrders: builder.query<ICustomerOrdersResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}/orders`, method: "get" }),
    }),

    customerSubscriptions: builder.query<ICustomerSubscriptionsResponse, number>({
      query: (userId) => ({ url: `${ADMIN_CUSTOMERS}/${userId}/subscriptions`, method: "get" }),
    }),

    abandonedCartDashboard: builder.query<IAbandonedCartDashboardResponse, void>({
      query: () => ({ url: ABANDONED_CARTS_DASHBOARD, method: "get" }),
      providesTags: ["abandonedCart"],
    }),

    abandonedCart: builder.query<IAbandonedCartListResponse, IAbandonedCartListRequest>({
      query: (data) => ({ url: ABANDONED_CARTS, method: "get", data }),
      providesTags: ["abandonedCart"],
    }),

    abandonedCartById: builder.query<IAbandonedCartDetailsResponse, number>({
      query: (userId) => ({ url: `${ABANDONED_CARTS}/${userId}`, method: "get" }),
      providesTags: ["abandonedCart"],
    }),

    sendReminder: builder.mutation<IWalletCreditResponse, number>({
      query: (userId) => ({ url: `${ABANDONED_CARTS}/${userId}/send-reminder`, method: "post" }),
      invalidatesTags: ["abandonedCart"],
    }),

    sendBulkReminders: builder.mutation<IWalletCreditResponse, ISendBulkReminderRequest>({
      query: (data) => ({ url: SEND_REMINDERS, method: "post", data }),
      invalidatesTags: ["abandonedCart"],
    }),

    impersonetCustomer : builder.mutation<string, IPersonetRequest>({
      query: (data) => ({ url: IMPERSONATE_CUSTOMER, method: "post" ,data:data}),
      invalidatesTags: ["abandonedCart"],
    }),

       AbandonedComment : builder.mutation<string, {abandonedCartId:number,data:IAbandonedCartRequest}>({
      query: ({abandonedCartId,data}) => ({ url:`${ABANDONED_CARTS}/${abandonedCartId}/comments`, method: "post" ,data:data}),
      invalidatesTags: ["abandonedCart"],
    }),
  }),
});

export const {
  useLazyGetCustomersForAdminQuery,
  useLazyGetCustomersForAdminByIdQuery,
  useLazyGetCustomerWalletByIdQuery,
  useCreditCustomerWalletMutation,
  useDebitCustomerWalletMutation,
  useLazyWalletTransactionQuery,
  useLazyUserRewardPointsQuery,
  useAddCustomerRewardPointsMutation,
  useDeductCustomerRewardPointsMutation,
  useLazyRewardTransactionQuery,
  useLazyCustomerAddressesQuery,
  useLazyCustomerAddressesByIdQuery,
  useUpdateCustomerAddressesByIdMutation,
  useLazyCustomerPetsQuery,
  useLazyCustomerPetsByIdQuery,
  useUpdateCustomerPetsByIdMutation,
  useLazyCustomerOrdersQuery,
  useLazyCustomerSubscriptionsQuery,
  useLazyAbandonedCartByIdQuery,
  useLazyAbandonedCartDashboardQuery,
  useLazyAbandonedCartQuery,
  useSendBulkRemindersMutation,
  useSendReminderMutation,
  useImpersonetCustomerMutation,
  useAbandonedCommentMutation
} = adminAbandonedCartAPi;